// Định dạng LƯU TRỮ chung cho mọi hệ điều hành, ví dụ: "Mod + Shift + S"
//   Mod   = Ctrl trên Windows/Linux, ⌘ Command trên Mac
//   Alt   = Alt trên Windows/Linux,  ⌥ Option trên Mac
//   Shift = Shift trên cả hai
// Khi HIỂN THỊ mới đổi sang ký hiệu của từng hệ điều hành.

const detectMac = () => {
  if (typeof navigator === "undefined") return false;
  const platform =
    navigator.userAgentData?.platform ||
    navigator.platform ||
    navigator.userAgent ||
    "";
  return /mac|iphone|ipad|ipod/i.test(platform);
};

export const IS_MAC = detectMac();

const SEPARATOR = " + ";
const MODIFIERS = ["Mod", "Alt", "Shift"];

// Các cách viết khác nhau (kể cả dữ liệu cũ đã lưu) → tên chuẩn
const MODIFIER_ALIASES = {
  mod: "Mod",
  ctrl: "Mod",
  control: "Mod",
  meta: "Mod",
  cmd: "Mod",
  command: "Mod",
  "⌘": "Mod",
  alt: "Alt",
  option: "Alt",
  opt: "Alt",
  "⌥": "Alt",
  shift: "Shift",
  "⇧": "Shift",
};

// e.code của các phím không phải chữ/số → tên chuẩn
const CODE_TO_KEY = {
  Space: "Space",
  Enter: "Enter",
  NumpadEnter: "Enter",
  Backspace: "Backspace",
  Delete: "Delete",
  Insert: "Insert",
  Home: "Home",
  End: "End",
  PageUp: "PageUp",
  PageDown: "PageDown",
  ArrowUp: "ArrowUp",
  ArrowDown: "ArrowDown",
  ArrowLeft: "ArrowLeft",
  ArrowRight: "ArrowRight",
  Minus: "-",
  Equal: "=",
  BracketLeft: "[",
  BracketRight: "]",
  Backslash: "\\",
  Semicolon: ";",
  Quote: "'",
  Comma: ",",
  Period: ".",
  Slash: "/",
  Backquote: "`",
};

const COMMON_LABELS = {
  ArrowUp: "↑",
  ArrowDown: "↓",
  ArrowLeft: "←",
  ArrowRight: "→",
};
const MAC_LABELS = {
  Mod: "Cmd",
  Alt: "Option",
  Shift: "Shift",
  Enter: "↩",
  Backspace: "⌫",
  Delete: "⌦",
};
const WIN_LABELS = { Mod: "Ctrl", Alt: "Alt", Shift: "Shift" };

// Dùng e.code (vị trí phím vật lý) thay vì e.key, vì:
// - Option trên Mac biến ký tự (Option+S → "ß")
// - Shift biến ký tự (Shift+1 → "!")
const getMainKey = (code = "") => {
  if (/^Key[A-Z]$/.test(code)) return code.slice(3); // KeyS → S
  if (/^Digit\d$/.test(code)) return code.slice(5); // Digit1 → 1
  if (/^Numpad\d$/.test(code)) return code.slice(6); // Numpad1 → 1
  if (/^F\d{1,2}$/.test(code)) return code; // F1..F12
  return CODE_TO_KEY[code] || null;
};

/** Chuyển KeyboardEvent → mảng phím chuẩn, ví dụ ["Mod", "Shift", "S"] */
export const eventToKeys = (e) => {
  const keys = [];
  // Mac: ⌘ là phím tắt chính, bỏ qua phím Control.
  // Windows: Ctrl là phím tắt chính, bỏ qua phím Windows (⊞).
  if (IS_MAC ? e.metaKey : e.ctrlKey) keys.push("Mod");
  if (e.altKey) keys.push("Alt");
  if (e.shiftKey) keys.push("Shift");
  const mainKey = getMainKey(e.code);
  if (mainKey) keys.push(mainKey);
  return keys;
};

/** Chuẩn hoá chuỗi "Ctrl + S" / mảng ["Meta", "S"] / ... → ["Mod", "S"] */
export const normalizeKeys = (input) => {
  if (!input) return [];
  const parts = Array.isArray(input) ? input : String(input).split("+");
  const modifiers = new Set();
  let mainKey = null;

  parts
    .map((p) => String(p).trim())
    .filter(Boolean)
    .forEach((part) => {
      const modifier = MODIFIER_ALIASES[part.toLowerCase()];
      if (modifier) modifiers.add(modifier);
      else mainKey = part.length === 1 ? part.toUpperCase() : part;
    });

  // Luôn sắp theo thứ tự cố định để "Shift + Mod + S" và "Mod + Shift + S" là một
  return [
    ...MODIFIERS.filter((m) => modifiers.has(m)),
    ...(mainKey ? [mainKey] : []),
  ];
};

/** Chuỗi để lưu xuống backend / so sánh trùng lặp: "Mod + Shift + S" (hoặc null) */
export const serializeShortcut = (input) => {
  const keys = normalizeKeys(input);
  return keys.length > 0 ? keys.join(SEPARATOR) : null;
};

/** Tổ hợp hợp lệ phải kết thúc bằng một phím chính, không chỉ toàn phím bổ trợ */
export const isCompleteShortcut = (input) => {
  const keys = normalizeKeys(input);
  return keys.length > 0 && !MODIFIERS.includes(keys[keys.length - 1]);
};

/** Chuỗi hiển thị theo hệ điều hành: Mac "⌘ + ⇧ + S", Windows "Ctrl + Shift + S" */
export const formatShortcut = (input) => {
  const labels = IS_MAC ? MAC_LABELS : WIN_LABELS;
  return normalizeKeys(input)
    .map((k) => labels[k] || COMMON_LABELS[k] || k)
    .join(SEPARATOR);
};

/** Dùng ở nơi lắng nghe phím tắt để thực thi: matchShortcut(e, "Mod + S") */
export const matchShortcut = (e, shortcut) => {
  const target = serializeShortcut(shortcut);
  return !!target && serializeShortcut(eventToKeys(e)) === target;
};
